/** Pure Contexto game helpers (tested). */

export interface Guess {
  /** word as typed (lowercased) */
  w: string;
  /** distance rank: 0 = the secret word */
  d: number;
  /** true when this word came from a tip */
  t?: boolean;
}

/**
 * The API is 0-based (the secret word has distance 0); contexto.me displays
 * distance + 1 (the secret word is "1"). We store the raw API value and convert
 * for display so numbers match what people see on the site.
 */
export function rankOf(distance: number): number {
  return distance + 1;
}

/** Bar colour thresholds on the DISPLAYED rank — same as contexto.me's share emoji. */
export const GREEN_MAX = 300;
export const YELLOW_MAX = 1500;
/** Rank ceiling used for bar width; anything beyond is "very far". */
export const FAR = 5000;

export type Band = "green" | "yellow" | "red";

export function bandOf(distance: number): Band {
  const rank = rankOf(distance);
  if (rank <= GREEN_MAX) return "green";
  if (rank <= YELLOW_MAX) return "yellow";
  return "red";
}

/** 0..1 fill for the bar: 1 for the answer, ~0.04 floor far away. Log-ish feel. */
export function barFill(distance: number): number {
  const rank = rankOf(distance);
  if (rank <= 1) return 1;
  const capped = Math.min(rank, FAR);
  const fill = 1 - Math.log(capped) / Math.log(FAR);
  return Math.max(0.04, Math.min(1, fill));
}

export function parseGuesses(json: string): Guess[] {
  try {
    const v: unknown = JSON.parse(json);
    if (!Array.isArray(v)) return [];
    return v.filter(
      (g): g is Guess =>
        typeof g === "object" && g !== null && typeof (g as Guess).w === "string" && typeof (g as Guess).d === "number",
    );
  } catch {
    return [];
  }
}

export function bestDistance(guesses: Guess[]): number | null {
  let best: number | null = null;
  for (const g of guesses) if (best === null || g.d < best) best = g.d;
  return best;
}

/** Share-style counts: 🟩 ≤300, 🟨 ≤1500, 🟥 beyond. */
export function bandCounts(guesses: Guess[]): { green: number; yellow: number; red: number } {
  const c = { green: 0, yellow: 0, red: 0 };
  for (const g of guesses) c[bandOf(g.d)]++;
  return c;
}

/** Sorted for display: closest first; ties keep guess order. */
export function sortedGuesses(guesses: Guess[]): Guess[] {
  return guesses.map((g, i) => ({ g, i })).sort((a, b) => a.g.d - b.g.d || a.i - b.i).map((x) => x.g);
}

/**
 * Distance to request for a tip: contexto gives you a word closer than your best.
 * Halve the best (min 1); with no guesses, start in the warm zone.
 */
export function tipDistance(guesses: Guess[]): number {
  const best = bestDistance(guesses);
  if (best === null) return 299;
  if (best <= 1) return 1;
  return Math.max(1, Math.floor(best / 2));
}

export function normalizeWord(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

export function isPlausibleWord(word: string): boolean {
  // letters (any script), optional inner hyphen/apostrophe/space; 1-40 chars
  return /^[\p{L}][\p{L}'\- ]{0,38}[\p{L}]$|^[\p{L}]$/u.test(word);
}
